import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useDriverStore } from '../../stores/driverStore';
import { Driver } from '../../types';

const AddDriverScreen: React.FC = () => {
  const navigation = useNavigation();
  const { addDriver, loading } = useDriverStore();

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    licenseNumber: '',
    licenseExpiry: '',
    status: 'inactive' as Driver['status'],
    dailyTarget: '',
    deposit: '',
    nationality: '',
    notes: '',
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [showStatusModal, setShowStatusModal] = useState(false);

  const statusOptions = [
    { label: 'Inactive', value: 'inactive' },
    { label: 'Active', value: 'active' },
    { label: 'Suspended', value: 'suspended' },
  ];

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Driver name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Driver name must be at least 2 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Enhanced phone validation
      const phoneRegex = /^\+?[\d\s\-()]+$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = 'Please enter a valid phone number';
      } else {
        // Check if phone has enough digits (at least 7)
        const digitsOnly = formData.phone.replace(/\D/g, '');
        if (digitsOnly.length < 7) {
          newErrors.phone = 'Phone number must have at least 7 digits';
        } else if (digitsOnly.length > 15) {
          newErrors.phone = 'Phone number seems too long (max 15 digits)';
        }
      }
    }

    if (!formData.licenseNumber.trim()) {
      newErrors.licenseNumber = 'License number is required';
    } else if (formData.licenseNumber.trim().length < 3) {
      newErrors.licenseNumber = 'License number must be at least 3 characters';
    }

    if (!formData.licenseExpiry.trim()) {
      newErrors.licenseExpiry = 'License expiry is required';
    } else {
      // Basic date format validation (YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(formData.licenseExpiry)) {
        newErrors.licenseExpiry = 'Please enter date in YYYY-MM-DD format';
      } else {
        // Validate actual date
        const date = new Date(formData.licenseExpiry);
        if (isNaN(date.getTime())) {
          newErrors.licenseExpiry = 'Please enter a valid date';
        } else {
          // Check if date is not in the past
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (date < today) {
            newErrors.licenseExpiry = 'Warning: License expiry date is in the past';
          }
        }
      }
    }

    // Daily target validation
    if (formData.dailyTarget) {
      const targetNum = Number(formData.dailyTarget);
      if (isNaN(targetNum)) {
        newErrors.dailyTarget = 'Daily target must be a number';
      } else if (targetNum < 0) {
        newErrors.dailyTarget = 'Daily target cannot be negative';
      } else if (targetNum > 5000) {
        newErrors.dailyTarget = 'Daily target seems unusually high (max 5,000 AED)';
      }
    }

    // Deposit validation
    if (formData.deposit) {
      const depositNum = Number(formData.deposit);
      if (isNaN(depositNum)) {
        newErrors.deposit = 'Deposit must be a number';
      } else if (depositNum < 0) {
        newErrors.deposit = 'Deposit cannot be negative';
      } else if (depositNum > 50000) {
        newErrors.deposit = 'Deposit seems unusually high (max 50,000 AED)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Alert.alert(
        'Validation Error',
        'Please correct the errors in the form before submitting.',
        [{ text: 'OK' }]
      );
      return;
    }

    try {
      const driverData: Omit<Driver, 'id' | 'created_at'> = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        licenseNumber: formData.licenseNumber.trim(),
        licenseExpiry: formData.licenseExpiry.trim(),
        status: formData.status,
        dailyTarget: formData.dailyTarget ? Number(formData.dailyTarget) : undefined,
        deposit: formData.deposit ? Number(formData.deposit) : undefined,
        nationality: formData.nationality.trim() || undefined,
        notes: formData.notes.trim() || undefined,
      };

      await addDriver(driverData);
      
      Alert.alert(
        'Success',
        `Driver ${driverData.name} has been added successfully!`,
        [
          {
            text: 'OK',
            onPress: () => {
              // Navigate to Drivers list
              if (navigation.canGoBack()) {
                navigation.goBack();
              } else {
                // If can't go back, navigate to Drivers route
                (navigation as any).navigate?.('Drivers');
              }
            },
          },
        ]
      );
    } catch (error: any) {
      Alert.alert(
        'Error',
        `Failed to add driver: ${error.message || 'Unknown error'}`,
        [{ text: 'OK' }]
      );
    }
  };

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <KeyboardAvoidingView 
      style={styles.container} 
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.header}>
          <Text style={styles.title}>Add New Driver</Text>
          <Text style={styles.subtitle}>Enter driver details</Text>
        </View>

        <View style={styles.formContainer}>
          {/* Driver Name */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Driver Name *</Text>
            <TextInput
              style={[styles.input, errors.name && styles.inputError]}
              value={formData.name}
              onChangeText={(value) => updateFormData('name', value)}
              placeholder="e.g., Ahmad Ali"
              maxLength={100}
            />
            {errors.name && (
              <Text style={styles.errorText}>{errors.name}</Text>
            )}
          </View>

          {/* Phone */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Phone Number *</Text>
            <TextInput
              style={[styles.input, errors.phone && styles.inputError]}
              value={formData.phone}
              onChangeText={(value) => updateFormData('phone', value)}
              placeholder="e.g., +971501234567"
              keyboardType="phone-pad"
              maxLength={20}
            />
            {errors.phone && (
              <Text style={styles.errorText}>{errors.phone}</Text>
            )}
          </View>

          {/* License Number */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>License Number *</Text>
            <TextInput
              style={[styles.input, errors.licenseNumber && styles.inputError]}
              value={formData.licenseNumber}
              onChangeText={(value) => updateFormData('licenseNumber', value)}
              placeholder="e.g., DL123456789"
              maxLength={20}
            />
            {errors.licenseNumber && (
              <Text style={styles.errorText}>{errors.licenseNumber}</Text>
            )}
          </View>

          {/* License Expiry */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>License Expiry *</Text>
            <TextInput
              style={[styles.input, errors.licenseExpiry && styles.inputError]}
              value={formData.licenseExpiry}
              onChangeText={(value) => updateFormData('licenseExpiry', value)}
              placeholder="YYYY-MM-DD (e.g., 2025-12-31)"
              maxLength={10}
            />
            {errors.licenseExpiry && (
              <Text style={styles.errorText}>{errors.licenseExpiry}</Text>
            )}
          </View>

          {/* Status */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Status</Text>
            <TouchableOpacity
              style={styles.dropdown}
              onPress={() => setShowStatusModal(true)}
            >
              <Text style={styles.dropdownText}>
                {statusOptions.find(option => option.value === formData.status)?.label || 'Select Status'}
              </Text>
              <Text style={styles.dropdownArrow}>▼</Text>
            </TouchableOpacity>
          </View>

          {/* Nationality */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Nationality</Text>
            <TextInput
              style={styles.input}
              value={formData.nationality}
              onChangeText={(value) => updateFormData('nationality', value)}
              placeholder="e.g., UAE, India, Pakistan"
              maxLength={50}
            />
          </View>

          {/* Daily Target */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Daily Target (AED)</Text>
            <TextInput
              style={[styles.input, errors.dailyTarget && styles.inputError]}
              value={formData.dailyTarget}
              onChangeText={(value) => updateFormData('dailyTarget', value)}
              placeholder="e.g., 300"
              keyboardType="numeric"
            />
            {errors.dailyTarget && (
              <Text style={styles.errorText}>{errors.dailyTarget}</Text>
            )}
          </View>

          {/* Deposit */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Deposit (AED)</Text>
            <TextInput
              style={[styles.input, errors.deposit && styles.inputError]}
              value={formData.deposit}
              onChangeText={(value) => updateFormData('deposit', value)}
              placeholder="e.g., 1000"
              keyboardType="numeric"
            />
            {errors.deposit && (
              <Text style={styles.errorText}>{errors.deposit}</Text>
            )}
          </View>

          {/* Notes */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(value) => updateFormData('notes', value)}
              placeholder="Additional notes about the driver"
              multiline
              numberOfLines={3}
              maxLength={500}
            />
          </View>
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={() => navigation.goBack()}
            disabled={loading}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.submitButton, loading && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={loading}
          >
            <Text style={styles.submitButtonText}>
              {loading ? 'Adding...' : 'Add Driver'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>

      {/* Status Selection Modal */}
      <Modal
        visible={showStatusModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStatusModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Select Driver Status</Text>
            {statusOptions.map((option) => (
              <TouchableOpacity
                key={option.value}
                style={[
                  styles.modalOption,
                  formData.status === option.value && styles.modalOptionSelected
                ]}
                onPress={() => {
                  updateFormData('status', option.value);
                  setShowStatusModal(false);
                }}
              >
                <Text
                  style={[
                    styles.modalOptionText,
                    formData.status === option.value && styles.modalOptionTextSelected
                  ]}
                >
                  {option.label}
                </Text>
              </TouchableOpacity>
            ))}
            <TouchableOpacity
              style={styles.modalCancelButton}
              onPress={() => setShowStatusModal(false)}
            >
              <Text style={styles.modalCancelText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  scrollView: {
    flex: 1,
  },
  header: {
    backgroundColor: '#fff',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#666',
  },
  formContainer: {
    backgroundColor: '#fff',
    margin: 16,
    borderRadius: 12,
    padding: 20,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  inputError: {
    borderColor: '#ff4444',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  dropdown: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    backgroundColor: '#fafafa',
    padding: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dropdownText: {
    fontSize: 16,
    color: '#333',
  },
  dropdownArrow: {
    fontSize: 12,
    color: '#666',
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    marginTop: 4,
  },
  buttonContainer: {
    flexDirection: 'row',
    padding: 20,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#666',
  },
  submitButton: {
    flex: 1,
    backgroundColor: '#007bff',
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 20,
    minWidth: 280,
    maxWidth: '80%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalOption: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  modalOptionSelected: {
    backgroundColor: '#007bff20',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#333',
    textAlign: 'center',
  },
  modalOptionTextSelected: {
    color: '#007bff',
    fontWeight: '600',
  },
  modalCancelButton: {
    marginTop: 16,
    padding: 12,
    alignItems: 'center',
  },
  modalCancelText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '600',
  },
});

export default AddDriverScreen;